<template>
    <DForm @submit.prevent="preSubmitForm">
        <div class="customer-headings">
            <DCard
                :title="__('customers.customers.form_headings.customer_information')"
                padding
            >
                <DFormRow
                    :label="__('customers.customers.customer_type')"
                    :is-required="true"
                    :error="errors.customer_type"
                >
                    <DSelect
                        id="customer_type"
                        v-model="form.customer_type"
                        :options="customerTypeOptions"
                        :placeholder="__('customers.customers.select_customer_type')"
                        :is-valid="isFormFieldValid('customer_type')"
                        @update:value="handleCustomerTypeChange"
                    />
                </DFormRow>
                <DFormRow
                    v-if="showCompanyField(form.company)"
                    :label="__('customers.common.company')"
                    :is-required="companyValidation()"
                    :error="errors.company"
                >
                    <div class="check-container">
                        <DInput
                            id="company"
                            v-model="form.company"
                            :placeholder="__('customers.common.company')"
                            :is-valid="isFormFieldValid('company')"
                            maxlength="128"
                            @change="handleTaxExemptionRelevantFieldsChanged"
                        />
                        <DButton
                            v-if="isNorwayCustomer"
                            id="check-company"
                            class="check-button"
                            label="Check"
                            color="primary"
                            @click="checkCompanyLink(form.company)"
                        />
                    </div>
                </DFormRow>
                <DFormRow
                    v-if="showCompanyField(form.company2)"
                    :label="__('customers.common.company2')"
                    :error="errors.company2"
                    :hint="__('customers.common.company2_hint')"
                >
                    <DInput
                        id="company2"
                        v-model="form.company2"
                        :placeholder="__('customers.common.company2')"
                        :is-valid="isFormFieldValid('company2')"
                        maxlength="40"
                        @change="handleTaxExemptionRelevantFieldsChanged"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.customer_type === 'geschäftlich'"
                    :label="__('customers.common.legal_form')"
                    :error="errors.legal_form"
                >
                    <DSelect
                        id="legal_form"
                        v-model="form.legal_form"
                        :options="legalForms"
                        :is-valid="isFormFieldValid('legal_form')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.title')"
                    :error="errors.title"
                    :is-required="form.customer_type === 'privat' && $page.props.tenantSettings?.salutation_mandatory === true"
                >
                    <DSelect
                        id="title"
                        v-model="form.title"
                        :options="titles"
                        :placeholder="__('customers.common.select_title')"
                        :is-valid="isFormFieldValid('title')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.preceding_title')"
                    :error="errors.preceding_title"
                >
                    <DInput
                        id="preceding_title"
                        v-model="form.preceding_title"
                        :placeholder="__('customers.customers.preceding_title')"
                        :is-valid="isFormFieldValid('preceding_title')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.firstname')"
                    :is-required="firstNameValidation()"
                    :error="errors.firstname"
                >
                    <DInput
                        id="firstname"
                        v-model="form.firstname"
                        :placeholder="__('customers.common.firstname')"
                        :is-valid="isFormFieldValid('firstname')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.lastname')"
                    :is-required="lastNameValidation()"
                    :error="errors.lastname"
                >
                    <div class="check-container">
                        <DInput
                            id="check-full-name"
                            v-model="form.lastname"
                            :placeholder="__('customers.common.lastname')"
                            :is-valid="isFormFieldValid('lastname')"
                            maxlength="128"
                        />
                        <DButton
                            v-if="isNorwayCustomer"
                            id="check-full-name"
                            class="check-button"
                            label="Check"
                            color="primary"
                            @click="checkLink(fullName)"
                        />
                    </div>
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.following_title')"
                    :error="errors.following_title"
                >
                    <DInput
                        id="following_title"
                        v-model="form.following_title"
                        :placeholder="__('customers.customers.following_title')"
                        :is-valid="isFormFieldValid('following_title')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.birthday')"
                    :error="errors.birthday"
                >
                    <DDateFormatInput
                        id="birthday-date"
                        v-model="form.birthday"
                        format="YYYY-MM-DD"
                        type="date"
                        :placeholder="__('customers.customers.birthday')"
                        :is-valid="isFormFieldValid('birthday')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.vat_number')"
                    :error="errors.vat_number"
                    class="vat-number"
                >
                    <div class="vat-container">
                        <DInput
                            id="vat_number"
                            v-model="form.vat_number"
                            :placeholder="__('customers.customers.vat_number')"
                            :is-valid="isFormFieldValid('vat_number')"
                            :disabled="form.vat_number_exemption"
                            maxlength="45"
                            @change="resetVatNumberCheck"
                        />
                        <DButton
                            v-if="showVatCheckBtn"
                            id="vat-id-check"
                            class="vat-id-check"
                            :label="__('customers.customers.vat_number_check')"
                            color="primary"
                            :disabled="!userPermissions.includes('customer.edit')"
                            @click="openCheckVatIdModal"
                        />
                    </div>
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.sanity.label')"
                    :is-required="true"
                    :error="errors.sanity"
                >
                    <DSelect
                        id="sanity"
                        v-model="form.sanity"
                        :options="companySanities"
                        :placeholder="__('customers.common.sanity.label')"
                        :is-valid="isFormFieldValid('sanity')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.brand')"
                    :is-required="true"
                    :error="errors.brand_id"
                >
                    <DSelect
                        id="brand"
                        v-model="form.brand_id"
                        :options="brandOptions"
                        :placeholder="__('customers.customers.brand')"
                        :is-valid="isFormFieldValid('brand_id')"
                        @update:value="syncBrandValues"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.brand_customer_number')"
                    :error="errors.brand_customer_number"
                >
                    <DInput
                        id="brand_customer_number"
                        v-model="form.brand_customer_number"
                        :placeholder="__('customers.customers.brand_customer_number')"
                        :is-valid="isFormFieldValid('brand_customer_number')"
                        maxlength="45"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.customer_type === 'geschäftlich' && $page.props.tenantSettings?.enable_commercial_register_number === true"
                    :label="__('customers.customers.commercial_register_number')"
                    :error="errors.commercial_register_number"
                >
                    <DInput
                        id="commercial_register_number"
                        v-model="form.commercial_register_number"
                        :placeholder="__('customers.customers.commercial_register_number')"
                        :is-valid="isFormFieldValid('commercial_register_number')"
                        maxlength="100"
                    />
                </DFormRow>
                <DFormRow
                    v-if="useHeroldLocationIdOnCustomer"
                    :label="__('customers.customers.herold_location_id')"
                    :error="errors.herold_location_id"
                >
                    <HeroldLocationField
                        :value="form.herold_location_id"
                        :display-value="form.herold_location_name"
                        @update:model-value="handleLocationChange"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.customer_segments')"
                >
                    <DMultiSelect
                        id="customer_segments"
                        :options="customerSegmentOptions"
                        :search="true"
                        :selection="true"
                        options-empty-placeholder="No options placeholder"
                        :search-placeholder="__('customers.customers.assign_segments')"
                        selection-type="tags"
                        @update="updateSelectedSegments"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.customer_language')"
                >
                    <DSelect
                        id="customer_language"
                        v-model="form.customer_language"
                        :options="customerLanguageOptions"
                        :placeholder="__('customers.customers.customer_language')"
                        :is-valid="isFormFieldValid('customer_language')"
                    />
                </DFormRow>
                <BusinessIdField
                    v-if="ssnBusinessId && businessCustomer"
                    :business-id="form.business_id"
                    :errors="errors"
                    @business-id-value="businessIdValue"
                />
                <SSNField
                    v-if="ssnBusinessId && privateCustomer"
                    v-model="form.ssn"
                    :ssn="customer.ssn"
                    :errors="errors"
                    :ssn-check-on-edit="ssnCheckOnEdit"
                    @ssn-check="ssnCheck"
                />
                <DFormRow
                    v-if="form.customer_type === 'privat' && $page.props.tenantSettings?.norway_id === true"
                    :label="__('customers.customers.personal_id_norway')"
                    :error="errors.personal_id_norway"
                >
                    <DInput
                        id="personal_id_norway"
                        v-model="form.personal_id_norway"
                        :placeholder="__('customers.customers.personal_id_norway')"
                        :is-valid="isFormFieldValid('personal_id_norway')"
                        maxlength="11"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.customer_type === 'geschäftlich' && $page.props.tenantSettings?.norway_id === true"
                    :label="__('customers.customers.business_id_norway')"
                    :is-required="isNorwayCustomer && (form.id === null || form.country_code !== customer?.country_code || !!customer?.business_id_norway)"
                    :error="errors.business_id_norway"
                >
                    <div class="check-container">
                        <DInput
                            id="business_id_norway"
                            v-model="form.business_id_norway"
                            :placeholder="__('customers.customers.business_id_norway')"
                            :is-valid="isFormFieldValid('business_id_norway')"
                            maxlength="9"
                        />
                        <DButton
                            v-if="isNorwayCustomer"
                            id="check-business-id-norway"
                            class="check-button"
                            label="Check"
                            color="primary"
                            @click="checkLink(form.business_id_norway, true)"
                        />
                    </div>
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.customer_reference_number')"
                    :error="errors.customer_reference_number"
                >
                    <DInput
                        id="customer_reference_number"
                        v-model="form.customer_reference_number"
                        :placeholder="__('customers.customers.customer_reference_number')"
                        :is-valid="isFormFieldValid('customer_reference_number')"
                        maxlength="80"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.original_brand')"
                    :error="errors.original_brand_id"
                >
                    <DSelect
                        id="original_brand_id"
                        v-model="form.original_brand_id"
                        :options="originalBrands"
                        :placeholder="__('customers.customers.select_original_brand')"
                        :is-valid="isFormFieldValid('original_brand_id')"
                    />
                </DFormRow>
            </DCard>

            <DCard
                :title="__('customers.customers.form_headings.address')"
                padding
            >
                <DFormRow
                    :label="__('customers.common.address1')"
                    :is-required="isNorwayCustomer === false"
                    :error="errors.address1"
                >
                    <DInput
                        id="address1"
                        v-model="form.address1"
                        :placeholder="__('customers.common.address1')"
                        :is-valid="isFormFieldValid('address1')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.address2')"
                    :error="errors.address2"
                >
                    <DInput
                        id="address2"
                        v-model="form.address2"
                        :placeholder="__('customers.common.address2')"
                        :is-valid="isFormFieldValid('address2')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.postal_code')"
                    :is-required="true"
                    :error="errors.postal_code"
                >
                    <DInput
                        id="postal_code"
                        v-model="form.postal_code"
                        :placeholder="__('customers.common.postal_code')"
                        :is-valid="isFormFieldValid('postal_code')"
                        maxlength="12"
                        @focusout="checkPostalCode"
                    />
                </DFormRow>
                <DRow
                    v-if="isPostalCodeValid === false"
                    class="warning-tag"
                >
                    <DTag
                        color="primary"
                        :label="__('customers.customers.critical_actions.postal_code.message')"
                    />
                </DRow>
                <DFormRow
                    :label="__('customers.common.city')"
                    :is-required="true"
                    :error="errors.city"
                >
                    <DInput
                        id="city"
                        v-model="form.city"
                        :placeholder="__('customers.common.city')"
                        :is-valid="isFormFieldValid('city')"
                        maxlength="45"
                        @change="handleTaxExemptionRelevantFieldsChanged"
                        @focusout="checkPostalCode"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.country')"
                    :is-required="true"
                    :error="errors.country_code"
                >
                    <DSelect
                        id="country_code"
                        v-model="form.country_code"
                        :options="countries"
                        :placeholder="__('customers.common.select_country')"
                        :is-valid="isFormFieldValid('country_code')"
                        @change="handleTaxExemptionRelevantFieldsChanged"
                        @focusout="checkPostalCode"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isStateRequired(form.country_code)"
                    :label="form.country_code === 'ES' ? __('customers.common.province') : __('customers.common.state')"
                    :is-required="true"
                    :error="errors.state_id"
                >
                    <DSelect
                        id="state_id"
                        v-model="form.state_id"
                        :options="getStatesByCountryCode(form.country_code)"
                        :placeholder="form.country_code === 'ES' ? __('customers.common.select_province') : __('customers.common.select_state')"
                        :is-valid="isFormFieldValid('state_id')"
                    />
                </DFormRow>
            </DCard>

            <DCard
                :title="__('customers.customers.form_headings.communication')"
                padding
            >
                <DFormRow
                    :label="__('customers.common.email')"
                    :is-required="true"
                    :error="errors.email"
                >
                    <DInput
                        id="email"
                        v-model="form.email"
                        :placeholder="__('customers.common.email')"
                        :is-valid="isFormFieldValid('email')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.phone')"
                    :error="errors.phone"
                >
                    <div class="check-container check-container-phone">
                        <DPhoneInput
                            id="phone"
                            v-model="form.phone"
                            preserve-formatting
                            :phone-country-code-placeholder="phoneCountryCodePlaceholder"
                            :is-valid="isFormFieldValid('phone')"
                        />
                        <DButton
                            v-if="isNorwayCustomer"
                            id="check-phone"
                            class="check-button"
                            label="Check"
                            color="primary"
                            @click="checkLink(form.phone)"
                        />
                    </div>
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.mobilenumber')"
                    :error="errors.mobilenumber"
                >
                    <div class="check-container check-container-phone">
                        <DPhoneInput
                            id="mobile_number"
                            v-model="form.mobilenumber"
                            preserve-formatting
                            :phone-country-code-placeholder="phoneCountryCodePlaceholder"
                            :is-valid="isFormFieldValid('mobilenumber')"
                        />
                        <DButton
                            v-if="isNorwayCustomer"
                            id="check-mobile-number"
                            class="check-button"
                            label="Check"
                            color="primary"
                            @click="checkLink(form.mobilenumber)"
                        />
                    </div>
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.fax')"
                    :error="errors.fax"
                >
                    <DPhoneInput
                        id="fax"
                        v-model="form.fax"
                        :phone-country-code-placeholder="phoneCountryCodePlaceholder"
                        :is-valid="isFormFieldValid('fax')"
                    />
                </DFormRow>
            </DCard>

            <DCard
                :title="__('customers.customers.form_headings.invoice_settings')"
                padding
            >
                <DFormRow
                    v-if="tenantSettings.enable_divergent_payment_methods === false"
                    :label="__('customers.customers.payment_method')"
                    :is-required="true"
                    :error="errors.payment_method || paymentMethodError"
                >
                    <DSelect
                        id="payment_method"
                        v-model="form.payment_method"
                        :options="availablePaymentMethods"
                        :placeholder="__('customers.customers.select_payment_method')"
                        :is-valid="isFormFieldValid('payment_method')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="showSepaFields"
                    :label="__('customers.customers.iban')"
                    :is-required="sepaMethodSelected"
                    :error="errors.iban"
                    :hint="tenantSettings.enable_divergent_payment_methods === true ? __('customers.customers.payment_method_hint') : ''"
                >
                    <DInput
                        id="iban"
                        v-model="form.iban"
                        :placeholder="__('customers.customers.iban')"
                        :is-valid="isFormFieldValid('iban')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="showSepaFields"
                    :label="__('customers.customers.bic')"
                    :error="errors.bic"
                    :hint="tenantSettings.enable_divergent_payment_methods === true ? __('customers.customers.payment_method_hint') : ''"
                >
                    <DInput
                        id="bic"
                        v-model="form.bic"
                        :placeholder="__('customers.customers.bic')"
                        :is-valid="isFormFieldValid('bic')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="showSepaFields"
                    :label="__('customers.customers.account_owner')"
                    :is-required="sepaMethodSelected"
                    :error="errors.account_owner"
                    :hint="tenantSettings.enable_divergent_payment_methods === true ? __('customers.customers.payment_method_hint') : ''"
                >
                    <DInput
                        id="account_owner"
                        v-model="form.account_owner"
                        :placeholder="__('customers.customers.account_owner')"
                        :is-valid="isFormFieldValid('account_owner')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    v-if="tenantSettings.enable_divergent_payment_methods === false"
                    :label="__('customers.customers.payment_term')"
                    :error="errors.payment_term"
                >
                    <DSelect
                        id="payment_term"
                        v-model="form.payment_term"
                        :options="paymentTerms"
                        :placeholder="__('customers.customers.select_payment_term')"
                        :is-valid="isFormFieldValid('payment_term')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.billing_type')"
                    :is-required="true"
                    :error="errors.billing_type"
                >
                    <DSelect
                        id="billing_type"
                        v-model="form.billing_type"
                        :options="billingTypes"
                        :placeholder="__('customers.customers.billing_type')"
                        :is-valid="isFormFieldValid('billing_type')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.suppress_dunning_contract_termination')"
                    :error="errors.suppress_dunning_contract_termination"
                    :hint="__('customers.customers.suppress_dunning_contract_termination_description')"
                >
                    <DFormCheck
                        id="suppress_dunning_contract_termination"
                        v-model="form.suppress_dunning_contract_termination"
                        inline
                        :disabled="!userPermissions.includes('dunning.disabled')"
                        :is-valid="isFormFieldValid('suppress_dunning_contract_termination')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.dunning_block_until')"
                    :error="errors.dunning_block_until"
                >
                    <DDateFormatInput
                        id="dunning_block_until"
                        v-model="form.dunning_block_until"
                        format="YYYY-MM-DD"
                        type="date"
                        :placeholder="__('customers.customers.dunning_block_until')"
                        :disabled="!userPermissions.includes('dunning.stop')"
                        :is-valid="isFormFieldValid('dunning_block_until')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.tax_exemption')"
                    :error="errors.tax_exemption_id"
                >
                    <DSelect
                        id="tax_exemption"
                        v-model="form.tax_exemption_id"
                        :options="taxExemptions"
                        :placeholder="__('customers.customers.select_tax_exemption')"
                        :is-valid="isFormFieldValid('tax_exemption_id')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="showFinanceSystemDebtorId"
                    :label="isFinanceSystemNetsuite ? __('customers.customers.netsuite_id') : __('customers.customers.debtor_id')"
                    :hint="__('customers.customers.debtor_help')"
                    :error="errors.finance_system_debtor_id"
                >
                    <DInput
                        id="finance_system_debtor_id"
                        v-model="form.finance_system_debtor_id"
                        :placeholder="isFinanceSystemNetsuite ? __('customers.customers.netsuite_id') : __('customers.customers.debtor_id')"
                        :is-valid="isFormFieldValid('finance_system_debtor_id')"
                    />
                </DFormRow>

                <DFormRow
                    :label="__('customers.customers.invoice_delivery_type')"
                    :is-required="true"
                    :error="errors.invoice_delivery_type"
                >
                    <DSelect
                        id="invoice_delivery_type"
                        v-model="form.invoice_delivery_type"
                        :options="invoiceDeliveryTypeOptions"
                        :placeholder="__('customers.customers.invoice_delivery_type')"
                        :is-valid="isFormFieldValid('invoice_delivery_type')"
                        :selected="selectedInvoiceDeliveryType"
                        @update:modelValue="setElectronicDeliveryInputsVisibility"
                    />
                </DFormRow>

                <DFormRow
                    v-if="electronicInvoiceInputsVisible"
                    class="ps-5"
                    :is-required="isElectronicInvoiceIdRequired"
                    :label="__('customers.customers.electronic_invoice_id')"
                    :error="errors.electronic_invoice_id"
                >
                    <DInput
                        id="electronic_invoice_id"
                        v-model="form.electronic_invoice_id"
                        :placeholder="__('customers.customers.electronic_invoice_id')"
                        :is-valid="isFormFieldValid('electronic_invoice_id')"
                        maxlength="34"
                    />
                </DFormRow>

                <DFormRow
                    v-if="electronicInvoiceInputsVisible"
                    class="ps-5"
                    :is-required="true"
                    :label="__('customers.customers.electronic_invoicing_type')"
                    :error="errors.electronic_invoicing_type"
                    :hint="__('customers.customers.electronic_invoicing_type_hint')"
                >
                    <DSelect
                        id="electronic_invoicing_type"
                        v-model="form.electronic_invoicing_type"
                        :options="electronicInvoicingTypeOptions"
                        :placeholder="__('customers.customers.electronic_invoicing_type')"
                        :is-valid="isFormFieldValid('electronic_invoicing_type')"
                        :selected="form.electronic_invoicing_type"
                    />
                </DFormRow>

                <DFormRow
                    v-if="tenantSettings.online_service_fee === true"
                    :label="__('customers.customers.suppress_service_fee')"
                    :error="errors.suppress_service_fee"
                    :hint="__('customers.customers.suppress_service_fee_description')"
                >
                    <DDateFormatInput
                        id="suppress_service_fee"
                        v-model="form.suppress_service_fee"
                        format="YYYY-MM-DD"
                        type="date"
                        :placeholder="__('customers.customers.suppress_service_fee')"
                        :is-valid="isFormFieldValid('suppress_service_fee')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.invoice_csv')"
                    :error="errors.invoice_csv"
                    :hint="__('customers.customers.invoice_csv_description')"
                >
                    <DFormCheck
                        id="invoice_csv"
                        v-model="form.invoice_csv"
                        inline
                        :is-valid="isFormFieldValid('invoice_csv')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.split_invoices')"
                    :error="errors.split_invoices"
                    :hint="__('customers.customers.split_invoices_helpful_text')"
                >
                    <DFormCheck
                        id="split_invoices"
                        v-model="form.split_invoices"
                        inline
                        :is-valid="isFormFieldValid('split_invoices')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.invoice_aggregations.label')"
                    :hint="__('customers.customers.invoice_aggregations.description')"
                    :error="errors.invoice_aggregation"
                >
                    <DSelect
                        id="invoice_aggregation"
                        v-model="form.invoice_aggregation"
                        :options="invoiceAggregations"
                        :placeholder="__('customers.customers.invoice_aggregations.label')"
                        :is-valid="isFormFieldValid('invoice_aggregation')"
                    />
                </DFormRow>
            </DCard>

            <DCard
                :title="__('customers.customers.form_headings.alternative_billing_addresses')"
                padding
            >
                <DFormRow
                    :label="__('customers.customers.invoice_email')"
                    :error="errors.invoice_email"
                >
                    <DInput
                        id="invoice_email"
                        v-model="form.invoice_email"
                        :placeholder="__('customers.customers.invoice_email')"
                        :is-valid="isFormFieldValid('invoice_email')"
                        maxlength="128"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.customers.use_alternative_billing')"
                    :error="errors.use_alternative_billing"
                >
                    <DFormCheck
                        id="use_alternative_billing"
                        v-model="form.use_alternative_billing"
                        inline
                        :is-valid="isFormFieldValid('use_alternative_billing')"
                        @update:modelValue="handleTaxExemptionRelevantFieldsChanged"
                    />
                </DFormRow>
                <div v-if="form.use_alternative_billing === true">
                    <DFormRow
                        :label="__('customers.customers.invoice_company')"
                        :is-required="form.invoice_firstname === null
                            || form.invoice_firstname === ''
                            || form.invoice_lastname === null
                            || form.invoice_lastname === ''
                            || form.customer_type === 'geschäftlich'"
                        :error="errors.invoice_company"
                    >
                        <div class="check-container">
                            <DInput
                                id="invoice_company"
                                v-model="form.invoice_company"
                                :placeholder="__('customers.customers.invoice_company')"
                                :is-valid="isFormFieldValid('invoice_company')"
                                maxlength="128"
                                @change="handleTaxExemptionRelevantFieldsChanged"
                            />
                            <DButton
                                v-if="isNorwayCustomer"
                                id="check-invoice-company"
                                class="check-button"
                                label="Check"
                                color="primary"
                                @click="checkLink(form.invoice_company)"
                            />
                        </div>
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_company2')"
                        :error="errors.invoice_company2"
                        :hint="__('customers.customers.invoice_company2_hint')"
                    >
                        <DInput
                            id="invoice_company2"
                            v-model="form.invoice_company2"
                            :placeholder="__('customers.customers.invoice_company2')"
                            :is-valid="isFormFieldValid('invoice_company2')"
                            maxlength="128"
                            @change="handleTaxExemptionRelevantFieldsChanged"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_title')"
                        :error="errors.invoice_title"
                    >
                        <DSelect
                            id="invoice_title"
                            v-model="form.invoice_title"
                            :options="titles"
                            :placeholder="__('customers.common.select_title')"
                            :is-valid="isFormFieldValid('invoice_title')"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_firstname')"
                        :is-required="form.invoice_company === null || form.invoice_company === ''"
                        :error="errors.invoice_firstname"
                    >
                        <DInput
                            id="invoice_firstname"
                            v-model="form.invoice_firstname"
                            :placeholder="__('customers.customers.invoice_firstname')"
                            :is-valid="isFormFieldValid('invoice_firstname')"
                            maxlength="128"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_lastname')"
                        :is-required="form.invoice_company === null || form.invoice_company === ''"
                        :error="errors.invoice_lastname"
                    >
                        <div class="check-container">
                            <DInput
                                id="lastname"
                                v-model="form.invoice_lastname"
                                :placeholder="__('customers.customers.invoice_lastname')"
                                :is-valid="isFormFieldValid('invoice_lastname')"
                                maxlength="128"
                            />
                            <DButton
                                v-if="isNorwayCustomer"
                                id="check-invoice-full-name"
                                class="check-button"
                                label="Check"
                                color="primary"
                                @click="checkLink(fullCompanyName)"
                            />
                        </div>
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_address1')"
                        :is-required="true"
                        :error="errors.invoice_address1"
                    >
                        <DInput
                            id="address1"
                            v-model="form.invoice_address1"
                            :placeholder="__('customers.customers.invoice_address1')"
                            :is-valid="isFormFieldValid('invoice_address1')"
                            maxlength="128"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_address2')"
                        :error="errors.invoice_address2"
                    >
                        <DInput
                            id="address2"
                            v-model="form.invoice_address2"
                            :placeholder="__('customers.customers.invoice_address2')"
                            :is-valid="isFormFieldValid('invoice_address2')"
                            maxlength="128"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_postal_code')"
                        :is-required="true"
                        :error="errors.invoice_postal_code"
                    >
                        <DInput
                            id="invoice_postal_code"
                            v-model="form.invoice_postal_code"
                            :placeholder="__('customers.customers.invoice_postal_code')"
                            :is-valid="isFormFieldValid('invoice_postal_code')"
                            maxlength="12"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_city')"
                        :is-required="true"
                        :error="errors.invoice_city"
                    >
                        <DInput
                            id="invoice_city"
                            v-model="form.invoice_city"
                            :placeholder="__('customers.customers.invoice_city')"
                            :is-valid="isFormFieldValid('invoice_city')"
                            maxlength="45"
                            @change="handleTaxExemptionRelevantFieldsChanged"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_country')"
                        :is-required="true"
                        :error="errors.invoice_country_code"
                    >
                        <DSelect
                            id="invoice_country_code"
                            v-model="form.invoice_country_code"
                            :options="countries"
                            :placeholder="__('customers.common.select_country')"
                            :is-valid="isFormFieldValid('invoice_country_code')"
                            @change="handleTaxExemptionRelevantFieldsChanged"
                        />
                    </DFormRow>
                    <DFormRow
                        v-if="isStateRequired(form.invoice_country_code)"
                        :label="form.invoice_country_code === 'ES' ? __('customers.customers.invoice_province') : __('customers.customers.invoice_state')"
                        :is-required="true"
                        :error="errors.invoice_state_id"
                    >
                        <DSelect
                            id="invoice_state_id"
                            v-model="form.invoice_state_id"
                            :options="getStatesByCountryCode(form.invoice_country_code)"
                            :placeholder="form.invoice_country_code === 'ES' ? __('customers.common.select_province') : __('customers.common.select_state')"
                            :is-valid="isFormFieldValid('invoice_state_id')"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_mobilenumber')"
                        :error="errors.invoice_mobilenumber"
                    >
                        <div class="check-container check-container-phone">
                            <DPhoneInput
                                id="invoice_mobilenumber"
                                v-model="form.invoice_mobilenumber"
                                :phone-country-code-placeholder="phoneCountryCodePlaceholder"
                                :is-valid="isFormFieldValid('invoice_mobilenumber')"
                            />
                            <DButton
                                v-if="isNorwayCustomer"
                                id="check-mobile-number"
                                class="check-button"
                                label="Check"
                                color="primary"
                                @click="checkLink(form.invoice_mobilenumber)"
                            />
                        </div>
                    </DFormRow>
                    <DFormRow
                        v-if="useHeroldLocationIdOnCustomer"
                        :label="__('customers.customers.invoice_herold_location_id_billing')"
                        :error="errors.invoice_herold_location_id_billing"
                    >
                        <HeroldLocationField
                            :value="form.invoice_herold_location_id_billing"
                            :display-value="form.invoice_herold_location_name_billing"
                            @update:model-value="handleInvoiceLocationChange"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.invoice_vat_number')"
                        :error="errors.invoice_vat_number"
                        class="mb-4"
                    >
                        <div class="vat-container">
                            <DInput
                                id="invoice_vat_number"
                                v-model="form.invoice_vat_number"
                                :placeholder="__('customers.customers.invoice_vat_number')"
                                :is-valid="isFormFieldValid('invoice_vat_number')"
                                :disabled="form.invoice_vat_number_exemption"
                                maxlength="45"
                            />
                            <DButton
                                v-if="showInvoiceVatCheckBtn"
                                id="vat-id-check-billing"
                                class="vat-id-check"
                                :label="__('customers.customers.vat_number_check')"
                                color="primary"
                                :disabled="!userPermissions.includes('customer.edit')"
                                @click="openCheckVatIdModal"
                            />
                        </div>
                    </DFormRow>
                </div>
            </DCard>

            <DCard
                :title="__('customers.customers.form_headings.newsletter')"
                padding
            >
                <DFormRow
                    v-for="communicationSubscription in communicationSubscriptions"
                    :key="communicationSubscription"
                    :label="__('customers.customers.newsletter_' + communicationSubscription)"
                >
                    <DFormCheck
                        :id="`newsletter_${communicationSubscription}`"
                        inline
                        :checked="form.communication_subscriptions.includes(communicationSubscription)"
                        @change="changeCommunicationSubscriptions(communicationSubscription)"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('customers.common.telemarketing')"
                    :error="errors.telemarketing"
                >
                    <DFormCheck
                        id="telemarketing"
                        v-model="form.telemarketing"
                        inline
                        :is-valid="isFormFieldValid('telemarketing')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="tenantSettings.send_payment_confirmation_email === true"
                    :label="__('customers.common.payment_confirmation.title')"
                    :error="errors.send_payment_confirmation_email"
                >
                    <DFormCheck
                        id="send_payment_confirmation_email"
                        v-model="form.send_payment_confirmation_email"
                        inline
                        :is-valid="isFormFieldValid('send_payment_confirmation_email')"
                    />
                </DFormRow>
            </DCard>

            <DCard
                :title="__('customers.customers.form_headings.other_settings')"
                padding
            >
                <DFormRow
                    :label="__('customers.customers.is_reseller')"
                    :error="errors.is_reseller"
                >
                    <DFormCheck
                        id="is_reseller"
                        v-model="form.is_reseller"
                        inline
                        :is-valid="isFormFieldValid('is_reseller')"
                    />
                </DFormRow>

                <DFormRow
                    :label="__('customers.customers.orders_blocked')"
                    :error="errors.orders_blocked"
                >
                    <DFormCheck
                        id="orders_blocked"
                        v-model="form.orders_blocked"
                        inline
                        :is-valid="isFormFieldValid('orders_blocked')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.orders_blocked"
                    :label="__('customers.customers.order_blocked_reason')"
                    :error="errors.order_blocked_reason"
                >
                    <DInput
                        id="order_blocked_reason"
                        v-model="form.order_blocked_reason"
                        type="text"
                        :placeholder="__('customers.customers.order_blocked_reason')"
                        :is-valid="isFormFieldValid('order_blocked_reason')"
                        @change="old_order_blocked_reason = form.order_blocked_reason"
                    />
                </DFormRow>
                <DFormRow
                    v-if="!customer.id"
                    :label="__('customers.customers.disable_welcome_email')"
                    :error="errors.disable_welcome_email"
                    :hint="__('customers.customers.disable_welcome_email_helpful_text')"
                >
                    <DFormCheck
                        id="disable_welcome_email"
                        v-model="form.disable_welcome_email"
                        inline
                        :is-valid="isFormFieldValid('disable_welcome_email')"
                    />
                </DFormRow>

                <DFormRow
                    v-if="form.id === null"
                    :label="__('customers.customers.market_region')"
                    :is-required="true"
                    :error="errors.market_region_id"
                >
                    <DSelect
                        id="market_region_id"
                        v-model="form.market_region_id"
                        :options="marketRegionOptions"
                        :placeholder="__('customers.customers.market_region')"
                        :is-valid="isFormFieldValid('market_region_id')"
                        @change="updateMarketRegion"
                    />
                </DFormRow>
            </DCard>
            <DFormControls
                v-model="form.activity_log_comment"
                :cancel-route="customer.id ? $route('customers.customers.show', {customer: customer.id}) : $route('customers.customers.index')"
                :dirty="form.isDirty"
                :show-change-reason-input="true"
                :show-incident-list="customer.id ? true : false"
                :customer-id="customer.id ?? 0"
                @submit="preSubmitForm"
                @setIncident="setIncident"
            />
            <DModal
                v-model:show="warn_modals.iban.show"
                :title="__('customers.customers.critical_actions.iban_change.title')"
                :message="__('customers.customers.critical_actions.iban_change.message')"
                is-confirm
                is-error
                @close="warn_modals.iban.show = false"
                @confirm="warnModalConfirmation('iban')"
            />
            <DModal
                v-model:show="warn_modals.vat.show"
                :title="__('customers.customers.critical_actions.vat_number.title')"
                is-confirm
                is-error
                :message="__('customers.customers.critical_actions.vat_number.message')"
                @confirm="warnModalConfirmation('vat')"
                @close="warn_modals.vat.show=false"
            />
            <DModal
                v-model:show="warn_modals.edi.show"
                :title="__('customers.customers.critical_actions.edi_number.title')"
                is-confirm
                is-error
                :message="__('customers.customers.critical_actions.edi_number.message')"
                @confirm="warnModalConfirmation('edi')"
                @close="warn_modals.edi.show=false"
            />
            <DModal
                v-model:show="warn_modals.postal_code.show"
                :title="__('customers.customers.critical_actions.postal_code.title')"
                is-confirm
                is-warning
                :message="__('customers.customers.critical_actions.postal_code.message')"
                @confirm="warnModalConfirmation('postal_code')"
                @close="warn_modals.postal_code.show=false"
            />
            <VatIdCheckModal
                v-if="showVatIdCheckModal"
                :create-form-data="form"
                :customer="customer"
                :show-modal="showVatIdCheckModal"
                edit-customer-form
                @close-modal="showVatIdCheckModal = false"
                @update-create-customer-vat-id="updateForm"
            />
            <DModal
                v-if="showOrderNoErrorModal === true"
                v-model:show="showOrderNoErrorModal"
                :title="__('customers.customers.critical_actions.order_no_error.title')"
                :message="errors.order_no"
                is-error
                @close="showOrderNoErrorModal = null"
            />
        </div>
    </DForm>
</template>

<script>
import VatIdCheckModal from '~@/Components/Customers/Customers/Modals/VatIdCheck.vue'
import { __ } from '~@/Composables/translation'
import { validateIBAN } from '~@/Composables/iban'

import HeroldLocationField from './HeroldLocationField.vue'
import BusinessIdField from '~@/Components/Customers/Customers/BusinessIdField.vue'
import SSNField from '~@/Components/Customers/Customers/SSNField.vue'

const paymentMethodSepa = 'sepa'
const paymentMethodSepaB2B = 'sepa-b2b'
const paymentMethodIdeal = 'ideal'

export default {
    name: 'CustomerForm',
    components: {
        SSNField,
        BusinessIdField,
        HeroldLocationField,
        VatIdCheckModal
    },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        countries: {
            type: Array,
            default: () => { return [] }
        },
        states: {
            type: Object,
            default: () => { return {} }
        },
        phoneCountryCodePlaceholder: {
            type: String,
            default: ''
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        titles: {
            type: Array,
            default: () => { return [] }
        },
        customerTypes: {
            type: Array,
            default: () => { return [] }
        },
        paymentTerms: {
            type: Array,
            default: () => { return [] }
        },
        paymentMethods: {
            type: Array,
            default: () => { return [] }
        },
        taxExemptions: {
            type: Array,
            default: () => { return [] }
        },
        billingTypes: {
            type: Array,
            default: () => { return [] }
        },
        customerSegments: {
            type: Array,
            default: () => { return [] }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        companySanities: {
            type: Array,
            default: () => { return [] }
        },
        invoiceAggregations: {
            type: Array,
            default: () => { return [] }
        },
        defaultBrand: {
            type: Number,
            default: 1
        },
        invoiceDeliveryTypes: {
            type: Array,
            default: () => { return [] }
        },
        electronicInvoicingTypes: {
            type: Array,
            default: () => { return [] }
        },
        selectedInvoiceDeliveryType: {
            type: String,
            default: ''
        },
        useHeroldLocationIdOnCustomer: {
            type: Boolean,
            default: () => { return false }
        },
        ssnBusinessId: {
            type: Boolean,
            default: () => { return false }
        },
        ssnCheckOnEdit: {
            type: Boolean,
            default: () => { return false }
        },
        customerLocation: {
            type: String,
            default: ''
        },
        orderNo: {
            type: String,
            default: ''
        },
        legalForms: {
            type: Array,
            default: () => { return [] }
        },
        originalBrands: {
            type: Array,
            default: () => { return [] }
        },
        communicationSubscriptions: {
            type: Array,
            default: () => { return [] }
        },
        activeCommunicationSubscriptions: {
            type: Array,
            default: () => { return [] }
        },
        communicationSubscriptionsApproved: {
            type: Boolean,
            default: false
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: false
        },
        marketRegionOptions: {
            type: Array,
            default: () => { return [] }
        },
        defaultMarketRegion: {
            type: Number,
            default: () => { return 1 }
        }
    },
    emits: ['form-submitted', 'tax-exemption-fetch'],
    data () {
        return {
            form: this.$inertia.form({
                firstname: this.customer?.firstname ?? '',
                lastname: this.customer?.lastname ?? '',
                title: this.customer?.title ?? null,
                company: this.customer?.company ?? '',
                company2: this.customer?.company2 ?? '',
                sanity: this.customer?.sanity ?? null,
                address1: this.customer?.address1 ?? null,
                address2: this.customer?.address2 ?? null,
                postal_code: this.customer?.postal_code ?? null,
                city: this.customer?.city ?? null,
                country_code: this.customer?.country_code ?? this.$page.props.defaultCountry,
                state_id: this.customer?.state_id ?? null,
                email: this.customer?.email ?? null,
                use_alternative_billing: this.customer?.use_alternative_billing ?? false,
                invoice_email: this.customer?.invoice_email ?? null,
                invoice_firstname: this.customer?.invoice_firstname ?? null,
                invoice_lastname: this.customer?.invoice_lastname ?? null,
                invoice_title: this.customer?.invoice_title ?? null,
                invoice_company: this.customer?.invoice_company ?? null,
                invoice_company2: this.customer?.invoice_company2 ?? null,
                invoice_address1: this.customer?.invoice_address1 ?? null,
                invoice_address2: this.customer?.invoice_address2 ?? null,
                invoice_postal_code: this.customer?.invoice_postal_code ?? null,
                invoice_city: this.customer?.invoice_city ?? null,
                invoice_country_code: this.customer?.invoice_country_code ?? this.$page.props.defaultCountry,
                invoice_state_id: this.customer?.invoice_state_id ?? null,
                invoice_mobilenumber: this.customer?.invoice_mobilenumber ?? null,
                invoice_herold_location_id_billing: this.customer?.invoice_herold_location_id_billing ?? null,
                invoice_herold_location_name_billing: this.customer?.invoice_herold_location_name_billing ?? null,
                invoice_vat_number: this.customer?.invoice_vat_number ?? null,
                invoice_vat_number_valid: this.customer?.invoice_vat_number_valid ?? false,
                invoice_vat_number_exemption: this.customer?.invoice_vat_number_exemption ?? false,
                invoice_vat_number_checked_at: this.customer?.invoice_vat_number_checked_at ?? null,
                phone: this.customer?.phone ?? null,
                mobilenumber: this.customer?.mobilenumber ?? null,
                phone_pin: this.customer?.phone_pin ?? null,
                fax: this.customer?.fax ?? null,
                brand_id: this.customer?.brand_id ?? this.defaultBrand,
                payment_method: this.customer?.payment_method ?? 'banktransfer',
                suppress_dunning_contract_termination: this.customer?.suppress_dunning_contract_termination ?? false,
                dunning_block_until: this.customer?.dunning_block_until ?? null,
                payment_term: this.customer?.payment_term ?? (this.paymentTerms.find(term => term.is_default === true)?.value ?? (this.paymentTerms[0].value ?? null)),
                billing_type: this.customer?.billing_type ?? null,
                invoice_delivery_type: this.selectedInvoiceDeliveryType,
                electronic_invoice_id: this.customer?.electronic_invoice_id ?? null,
                electronic_invoicing_type: this.customer?.electronic_invoicing_type ?? null,
                vat_number: this.customer?.vat_number ?? null,
                vat_number_valid: this.customer?.vat_number_valid ?? false,
                vat_number_exemption: this.customer?.vat_number_exemption ?? false,
                vat_number_checked_at: this.customer?.vat_number_checked_at ?? null,
                tax_exemption_id: this.customer?.tax_exemption_id ?? null,
                brand_customer_number: this.customer?.brand_customer_number ?? null,
                commercial_register_number: this.customer?.commercial_register_number ?? null,
                customer_type: this.customer?.customer_type ?? null,
                original_brand_id: this.customer?.original_brand_id ?? null,
                iban: this.customer?.active_sepa_mandate?.iban ?? this.getOrderIban(),
                bic: this.customer?.active_sepa_mandate?.bic ?? this.getOrderBic(),
                account_owner: this.customer?.active_sepa_mandate?.account_owner ?? this.getOrderAccountOwner(),
                is_reseller: this.customer?.is_reseller ?? false,
                customer_segments: this.customer?.customer_segments_ids ?? [],
                birthday: this.customer?.birthday,
                newsletter_marketing: this.customer?.newsletter_marketing,
                newsletter_technical: this.customer?.newsletter_technical,
                telemarketing: this.customer?.telemarketing ?? false,
                invoice_csv: this.customer?.invoice_csv,
                activity_log_comment: '',
                orders_blocked: this.customer?.orders_blocked ?? false,
                order_blocked_reason: this.customer?.order_blocked_reason ?? null,
                confirm_invalid_emails: false,
                id: this.customer?.id ?? null,
                disable_welcome_email: false,
                suppress_service_fee: this.customer?.suppress_service_fee ?? null,
                split_invoices: this.customer?.split_invoices ?? false,
                invoice_aggregation: this.customer?.invoice_aggregation ?? null,
                finance_system_debtor_id: this.customer?.finance_system_debtor_id ?? null,
                herold_location_id: this.customer.herold_location_id ?? null,
                herold_location_name: this.customer.herold_location_name ?? null,
                customer_language: this.customer?.customer_language ?? this.brands[0].defaultLanguage,
                business_id: this.customer?.business_id ?? null,
                ssn: this.customer?.ssn ?? null,
                personal_id_norway: this.customer?.personal_id_norway ?? null,
                business_id_norway: this.customer?.business_id_norway ?? null,
                customer_reference_number: this.customer?.customer_reference_number ?? null,
                order_no: this.orderNo ?? null,
                legal_form: this.customer?.legal_form ?? null,
                following_title: this.customer?.following_title ?? null,
                preceding_title: this.customer?.preceding_title ?? null,
                communication_subscriptions: this.activeCommunicationSubscriptions ?? [],
                incident_id: null,
                ignoreProperties: [],
                communication_subscriptions_approved: this.communicationSubscriptionsApproved ?? false,
                send_payment_confirmation_email: this.customer?.send_payment_confirmation_email ?? false,
                market_region_id: this.customer?.market_region_id ?? this.defaultMarketRegion,
            }),
            paymentMethodSepa: paymentMethodSepa,
            paymentMethodSepaB2B: paymentMethodSepaB2B,
            paymentMethodIdeal: paymentMethodIdeal,
            old_order_blocked_reason: this.customer?.order_blocked_reason ?? null,
            showIbanConfirmationModal: false,
            showVatConfirmationModal: false,
            availablePaymentMethods: this.paymentMethods ?? null,
            warn_modals: {
                vat: { confirmed: false, show: false, awaiting_confirmation: false },
                iban: { confirmed: false, show: false, awaiting_confirmation: false },
                order_no: { confirmed: false, show: false, awaiting_confirmation: false },
                edi: { confirmed: false, show: false, awaiting_confirmation: false },
                postal_code: { confirmed: false, show: false, awaiting_confirmation: false }
            },
            showVatIdCheckModal: false,
            selectedCustomerSegments: this.customer?.customer_segments_ids ?? [],
            paymentMethodError: '',
            electronicInvoiceInputsVisible: this.isInvoiceDeliveryTypeElectronicInvoice(this.selectedInvoiceDeliveryType),
            ssnBirthday: '',
            ssnValid: '',
            showOrderNoErrorModal: false,
            defaultFormValues: [],
            isPostalCodeValid: true,
            showPostalCodeWarningModal: false
        }
    },
    computed: {
        customerTypeOptions () {
            const types = this.customerTypes

            for (const index in types) {
                if (types[index].label.length !== 0) {
                    types[index].label = this.__(`customers.customers.customer_types.${types[index].value}`)
                }
            }

            return types
        },
        invoiceDeliveryTypeOptions () {
            const customerType = this.form.customer_type
            const types = this.invoiceDeliveryTypes
            const availableTypes = []
            const businessCustomerAvailability = ['business_customers', 'all_customers']
            const privateCustomersAvailability = ['private_customers', 'all_customers']

            if (customerType === 'geschäftlich') {
                types.forEach(item => {
                    if (businessCustomerAvailability.includes(item.availability)) {
                        availableTypes.push({
                            value: item.value,
                            label: item.label
                        })
                    }
                })
            }

            if (customerType === 'privat') {
                types.forEach(item => {
                    if (privateCustomersAvailability.includes(item.availability)) {
                        availableTypes.push({
                            value: item.value,
                            label: item.label
                        })
                    }
                })
            }

            return availableTypes
        },
        customerSegmentOptions () {
            return this.customerSegments.map((option) => ({ ...option, selected: this.selectedCustomerSegments.includes(option.value) }))
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        showVatCheckBtn () {
            return this.isBusinessCustomerFromEuCountry(this.form.country_code)
        },
        showInvoiceVatCheckBtn () {
            return this.isBusinessCustomerFromEuCountry(this.form.invoice_country_code)
        },
        showFinanceSystemDebtorId () {
            return this.userPermissions.includes('customers.manage_debtor')
        },
        brandOptions () {
            return this.brands.map(item => {
                return {
                    value: item.id,
                    label: item.name
                }
            })
        },
        customerLanguageOptions () {
            const brand = this.brands.filter(item => item.id === parseInt(this.form.brand_id))[0]

            return brand.languages.map(item => {
                return {
                    value: item.value,
                    label: item.label
                }
            })
        },
        businessCustomer () {
            return this.form.customer_type === 'geschäftlich'
        },
        privateCustomer () {
            return this.form.customer_type === 'privat'
        },
        tenantSettings () {
            return this.$page.props.tenantSettings
        },
        isElectronicInvoiceIdRequired () {
            return this.form.electronic_invoicing_type === 'EDI' ||
                this.form.electronic_invoicing_type === 'IBAN'
        },
        fullName () {
            return `${this.form.firstname ?? ''}+${this.form.lastname}`
        },
        fullCompanyName () {
            return `${this.form.invoice_firstname ?? ''}+${this.form.invoice_lastname}`
        },
        isNorwayCustomer () {
            return this.form.country_code === 'NW' || this.form.country_code === 'NO'
        },
        showSepaFields () {
            return this.form.payment_method === paymentMethodSepa ||
                this.form.payment_method === paymentMethodSepaB2B ||
                this.form.payment_method === paymentMethodIdeal ||
                this.tenantSettings.enable_divergent_payment_methods === true
        },
        sepaMethodSelected () {
            return this.form.payment_method === paymentMethodSepa ||
                this.form.payment_method === paymentMethodSepaB2B ||
                this.form.payment_method === paymentMethodIdeal
        },
        electronicInvoicingTypeOptions () {
            return [
                { value: null, label: '' },
                ...this.electronicInvoicingTypes
            ]
        }
    },
    watch: {
        'form.use_alternative_billing' (newVal) {
            if (newVal === true) {
                this.form.invoice_firstname = this.customer?.firstname ?? null
                this.form.invoice_lastname = this.customer?.lastname ?? null
                this.form.invoice_title = this.customer?.title ?? null
                this.form.invoice_company = this.customer?.company ?? null
                this.form.invoice_company2 = this.customer?.company2 ?? null
                this.form.invoice_address1 = this.customer?.address1 ?? null
                this.form.invoice_address2 = this.customer?.address2 ?? null
                this.form.invoice_postal_code = this.customer?.postal_code ?? null
                this.form.invoice_city = this.customer?.city ?? null
                this.form.invoice_country_code = this.customer?.country_code ?? this.$page.props.defaultCountry
                this.form.invoice_state_id = this.customer?.state_id ?? null
                this.form.invoice_vat_number = this.customer?.vat_number ?? null
                this.updateMarketRegionBasedOnContext()
            } else {
                this.form.invoice_firstname = null
                this.form.invoice_lastname = null
                this.form.invoice_title = null
                this.form.invoice_company = null
                this.form.invoice_company2 = null
                this.form.invoice_address1 = null
                this.form.invoice_address2 = null
                this.form.invoice_postal_code = null
                this.form.invoice_city = null
                this.form.invoice_country_code = this.$page.props.defaultCountry
                this.form.invoice_state_id = null
                this.form.invoice_vat_number = null
                this.updateMarketRegionBasedOnContext()
            }
        },
        'form.orders_blocked' (newVal) {
            if (newVal === false) {
                this.form.order_blocked_reason = null
            } else {
                this.form.order_blocked_reason = this.old_order_blocked_reason
            }
        },
        'errors' () {
            this.showOrderNoErrorModal = !!this.errors.order_no
        },
        'form.country_code' () {
            // we want to keep state id as long as other country with state selection is picked
            // another sanity check takes place before submit
            const countryCode = this.form.country_code
            const stateId = this.form.state_id
            if (this.isStateRequired(countryCode) && !this.isStateIdOfCountry(countryCode, stateId)) {
                this.form.state_id = null
            }
            this.updateMarketRegionBasedOnContext()
        },
        'form.invoice_country_code' () {
            // we want to keep state id as long as other country with state selection is picked
            // another sanity check takes place before submit
            const invoiceCountryCode = this.form.invoice_country_code
            const invoiceStateId = this.form.invoice_state_id
            if (this.isStateRequired(invoiceCountryCode) && !this.isStateIdOfCountry(invoiceCountryCode, invoiceStateId)) {
                this.form.invoice_state_id = null
            }
            this.updateMarketRegionBasedOnContext()
        }
    },
    mounted () {
        this.defaultFormValues = this.form.data()
        this.updateMarketRegionBasedOnContext()
    },
    created () {
        this.checkPostalCode()
    },
    methods: {
        getOrderIban () {
            return this.orderNo ? this.customer?.iban : null
        },
        getOrderBic () {
            return this.orderNo ? this.customer?.bic : null
        },
        getOrderAccountOwner () {
            return this.orderNo ? this.customer?.account_owner : null
        },
        getStatesByCountryCode (countryCode) {
            return Object.values(this.$page.props.states[countryCode] ?? [])
        },
        isStateRequired (countryCode) {
            const country = this.$page.props.countries.find(country => country.value === countryCode)
            return !!country?.state_required
        },
        isStateIdOfCountry (countryCode, stateId) {
            if (!countryCode || !stateId) return false
            const states = this.getStatesByCountryCode(countryCode)
            return !!states.some(state => state.value === parseInt(stateId))
        },
        isBusinessCustomerFromEuCountry (countryCode) {
            return this.form.customer_type === 'geschäftlich' && this.countries.find(e => e.value === countryCode)?.is_eu === true
        },
        preSubmitForm () {
            this.warn_modals.iban.show = false
            this.warn_modals.iban.awaiting_confirmation = false
            this.warn_modals.vat.show = false
            this.warn_modals.vat.awaiting_confirmation = false
            this.warn_modals.edi.show = false
            this.warn_modals.edi.awaiting_confirmation = false
            this.warn_modals.postal_code.show = false
            this.warn_modals.postal_code.awaiting_confirmation = false

            if (this.isPostalCodeValid === false) {
                this.warn_modals.postal_code.show = true
                this.warn_modals.postal_code.awaiting_confirmation = true
            }
            if (this.checkIBANisChanging() && !this.warn_modals.iban.confirmed) {
                this.warn_modals.iban.show = true
                this.warn_modals.iban.awaiting_confirmation = true
            }
            if (this.privateCustomerHasVatNumber() && !this.warn_modals.vat.confirmed) {
                this.warn_modals.vat.show = true
                this.warn_modals.vat.awaiting_confirmation = true
            }
            if (
                this.form.electronic_invoice_id &&
                this.form.invoice_delivery_type === 'electronic_invoice' &&
                !this.validElectronicInvoiceIdAndType() &&
                !this.warn_modals.edi.confirmed
            ) {
                this.warn_modals.edi.show = true
                this.warn_modals.edi.awaiting_confirmation = true
            }

            if (this.form.state_id) {
                if (!this.isStateIdOfCountry(this.form.country_code, this.form.state_id)) {
                    this.form.state_id = null
                }
            }

            if (this.form.invoice_state_id) {
                if (!this.isStateIdOfCountry(this.form.invoice_country_code, this.form.invoice_state_id)) {
                    this.form.invoice_state_id = null
                }
            }

            if (!this.getModalsAwaitingConfirmation().length) {
                this.submitForm()
            }
            if (this.ssnValid) {
                this.form.birthday = this.ssnBirthday
            }
        },
        warnModalConfirmation (type) {
            this.warn_modals[type].confirmed = true
            this.warn_modals[type].awaiting_confirmation = false
            if (!this.getModalsAwaitingConfirmation().length) {
                this.submitForm()
            }
        },
        getModalsAwaitingConfirmation () {
            return Object.keys(this.warn_modals).filter(key => {
                return this.warn_modals[key].awaiting_confirmation === true
            })
        },
        resetWarnModals () {
            Object.keys(this.warn_modals).forEach(key => {
                this.warn_modals[key].confirmed = false
            })
        },
        submitForm () {
            const updatedForm = this.form.data()

            const updatedProperties = []

            // find properties that were updated
            for (const key in this.defaultFormValues) {
                if (Object.hasOwn(updatedForm, key) && Object.hasOwn(this.defaultFormValues, key)) {
                    if (updatedForm[key] !== this.defaultFormValues[key]) {
                        updatedProperties.push(key)
                    }
                }
            }

            this.resetWarnModals()
            if (this.ssnBusinessId === false) {
                this.form.ssn = null
                this.form.business_id = null
            } else {
                if (this.businessCustomer) {
                    this.form.ssn = null
                }
                if (this.privateCustomer) {
                    this.form.business_id = null
                }
            }
            this.$emit('form-submitted', {
                form: this.form,
                updatedProperties: updatedProperties
            })
        },
        updateSelectedSegments (values) {
            this.form.customer_segments = values
        },
        checkIBANisChanging () {
            if (
                (this.form.payment_method === 'sepa' || this.form.payment_method === 'sepa-b2b' || this.form.payment_method === 'ideal') &&
                (this.customer.payment_method === 'sepa' || this.customer.payment_method === 'sepa-b2b' || this.form.payment_method === 'ideal') &&
                this.customer.id !== undefined &&
                this.form.iban !== this.customer?.active_sepa_mandate?.iban) {
                return true
            } else {
                return false
            }
        },
        privateCustomerHasVatNumber () {
            return this.form.customer_type === 'privat' && (this.form.vat_number || this.form.invoice_vat_number)
        },
        handleTaxExemptionRelevantFieldsChanged () {
            this.$emit('tax-exemption-fetch', this.form)
        },
        openCheckVatIdModal () {
            this.showVatIdCheckModal = true
        },
        updateForm (data) {
            this.form.vat_number = data.vat_number
            this.form.vat_number_valid = data.vat_number_valid
            this.form.vat_number_exemption = data.vat_number_exemption
            this.form.vat_number_checked_at = data.vat_number_checked_at
            this.form.invoice_vat_number = data.invoice_vat_number
            this.form.invoice_vat_number_valid = data.invoice_vat_number_valid
            this.form.invoice_vat_number_exemption = data.invoice_vat_number_exemption
            this.form.invoice_vat_number_checked_at = data.invoice_vat_number_checked_at
        },
        resetVatNumberCheck () {
            this.form.vat_number_valid = false
            this.form.vat_number_exemption = false
            this.form.vat_number_checked_at = null
        },
        syncBrandValues (brandId) {
            this.handleLanguageChange(brandId)
            if (this.tenantSettings.enable_divergent_payment_methods === false) {
                this.syncPaymentMethods()
            }
        },
        updateMarketRegion () {
            if (this.tenantSettings.enable_divergent_payment_methods === false) {
                this.syncPaymentMethods()
            }
        },
        handleLanguageChange (brandId) {
            this.form.customer_language = this.brands.filter(item => item.id === parseInt(brandId))[0].defaultLanguage
        },
        syncPaymentMethods () {
            this.paymentMethodError = null
            this.form.payment_method = null

            window.axios.get(this.$route('customers.customers.sync_payment_methods'), {
                params: {
                    brand_id: this.form.brand_id,
                    market_region_id: this.form.market_region_id,
                }
            })
                .then(response => {
                    this.availablePaymentMethods = response.data
                }).catch(() => {
                    this.availablePaymentMethods = []
                    this.paymentMethodError = __('customers.customers.responses.error.no_available_payment_methods')
                })
        },
        companyValidation () {
            if (this.form.customer_type === 'geschäftlich') {
                if (this.form.firstname.length > 0 && this.form.lastname.length > 0) {
                    return false
                } else if (this.form.firstname.length === 0 && this.form.lastname.length === 0) {
                    return true
                } else if (this.form.firstname.length === 0 || this.form.lastname.length === 0) {
                    return true
                }
            }

            return false
        },
        firstNameValidation () {
            if (this.form.customer_type === 'geschäftlich') {
                if (this.form.lastname.length > 0 && this.form.company.length > 0) {
                    return false
                } else if (this.form.lastname.length === 0 && this.form.company.length === 0) {
                    return true
                } else {
                    return this.form.lastname.length > 0 && this.form.company.length === 0 && this.form.firstname.length === 0
                }
            }

            return true
        },
        lastNameValidation () {
            if (this.form.customer_type === 'geschäftlich') {
                if (this.form.firstname.length > 0 && this.form.company.length > 0) {
                    return false
                } else if (this.form.firstname.length === 0 && this.form.company.length === 0) {
                    return true
                } else {
                    return this.form.firstname.length > 0 && this.form.company.length === 0 && this.form.lastname.length === 0
                }
            }

            return true
        },
        setElectronicDeliveryInputsVisibility (value) {
            this.electronicInvoiceInputsVisible = this.isInvoiceDeliveryTypeElectronicInvoice(value)
        },
        isInvoiceDeliveryTypeElectronicInvoice (value) {
            const electronicInvoice = 'electronic_invoice'
            return this.invoiceDeliveryTypes.some(
                (item) => (value === electronicInvoice && item.value === electronicInvoice)
            )
        },
        handleLocationChange (location) {
            this.form.herold_location_id = location.value
            this.form.herold_location_name = location.display_value
        },
        handleInvoiceLocationChange (location) {
            this.form.invoice_herold_location_id_billing = location.value
            this.form.invoice_herold_location_name_billing = location.display_value
        },
        businessIdValue (businessIdValue) {
            this.form.business_id = businessIdValue
        },
        ssnCheck (ssnValid, birthday, ssnCode) {
            this.form.ssn = ssnCode
            this.ssnValid = ssnValid
            this.ssnBirthday = birthday
        },
        handleCustomerTypeChange (value) {
            if (value !== 'geschäftlich') {
                this.form.company = ''
                this.form.company2 = ''
            }
        },
        showCompanyField (field) {
            return this.form.customer_type === 'geschäftlich' ||
                (field !== null && field !== '')
        },
        validElectronicInvoiceIdAndType () {
            if (this.form.electronic_invoicing_type === 'EDI') {
                return this.validateEdi()
            }

            if (this.form.electronic_invoicing_type === 'IBAN') {
                return validateIBAN(this.form.electronic_invoice_id)
            }

            return true
        },
        validateEdi () {
            const number = this.form.electronic_invoice_id.replace(/\s/g, '')

            // Check if the business_id is a substring of the number and length is good
            if (
                number.indexOf(this.form.business_id) === -1 &&
                number.length >= 12 &&
                number.length <= 17
            ) {
                return true
            }

            return false
        },
        setIncident (value) {
            this.form.incident_id = value
        },
        checkLink (value = null, businessIdNorway = false) {
            if (businessIdNorway === true) {
                window.open(
                    `https://w2.brreg.no/enhet/sok/detalj.jsp?orgnr=${value}`,
                    '_blank'
                )

                return
            }

            window.open(`https://www.1881.no/?query=${value}`, '_blank', 'noopener noreferrer')
        },
        checkCompanyLink (value = null) {
            const link = this.form.customer_type === 'privat' ? `https://www.1881.no/?query=${value}` : `https://www.1881.no/?query=${value}&type=firma`

            window.open(link, '_blank', 'noopener noreferrer')
        },
        checkPostalCode () {
            this.isPostalCodeValid = true

            // do not check if any of the fields is not populated
            if (this.form.country_code === null || this.form.postal_code === null || this.form.city === null) {
                return
            }

            window.axios.get(this.$route('customers.customers.check_postal_code'), {
                params: {
                    country_code: this.form.country_code,
                    postal_code: this.form.postal_code,
                    city: this.form.city
                }
            }).then(response => {
                this.isPostalCodeValid = response.data.result
            })
        },
        changeCommunicationSubscriptions (value) {
            if (this.form.communication_subscriptions.includes(value)) {
                this.form.communication_subscriptions = this.form.communication_subscriptions.filter(function (item) {
                    return item !== value
                })
            } else {
                this.form.communication_subscriptions.push(value)
            }
        },
        updateMarketRegionBasedOnContext () {
            const countryCode = this.form.use_alternative_billing
                ? this.form.invoice_country_code
                : this.form.country_code

            this.setMarketRegionFromCountry(countryCode)
        },
        setMarketRegionFromCountry (countryCode) {
            const regions = Object.values(this.$page.props.marketRegions)

            // Find all matching regions for this country (parent and child)
            const matchedRegions = Object.values(regions).filter(region =>
                region.countries?.some(c => c.country_code === countryCode)
            )

            // Fallback to default region if no match
            if (!matchedRegions.length) {
                this.form.market_region_id = this.defaultMarketRegion
                return
            }

            // Prefer the child (override) region
            const childRegion = matchedRegions.find(region => region.parent_id !== null)

            this.form.market_region_id = childRegion?.id ?? matchedRegions[0].id
        }
    }
}
</script>

<style lang="scss">
    .lessSpaceAbove {
        margin-top:-1.0em;
    }
    .overrideHint {
        color: red;
    }
    .customer-headings {
        display: grid;
        grid-gap: 25px;
    }
    .vat-container {
        display: flex;
        justify-content: center;
        align-items: center;
        .vat-id-check {
            margin-left: 10px;
            padding: 5px 15px!important;
        }
    }
    a.check {
        color: rgba(0, 0, 0, 0.6)!important;
        text-decoration: underline;
    }
    .warning-tag {
        margin: 5px 5px 25px;
    }
    .check-container {
        display: flex;
        justify-content: center;
        align-items: center;
        .check-button {
            margin-left: 10px;
        }
    }
    .check-container-phone {
        justify-content: flex-start;
    }
</style>
